/* tslint:disable-next-line: deprecation */
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { exhaustMap, map, mergeMap, observeOn, share } from 'rxjs/operators';
import { paths, key_user_info } from 'src/app/shared/helpers/constants';
import { SessionTimeoutService } from 'src/app/shared/services/session-timeout.service';
import { Event } from 'src/app/shared/enums/insights/event';

import { environment } from '../../../environments/environment';
import 'url-search-params-polyfill';

// import { environment } from 'src/ ../../../environments/environment';
import { IDatosCliente } from '../Entities/firmaElectronica';
import {
  defaultSessionRefreshTime,
  getFullRut,
  getPureRut,
  getRutDigit,
  setLocalStorage,
  removeLocalStorage,
  getLocalStorage,
} from '../helpers/helper';
import * as jwt_decode from 'jwt-decode';
import { KeycloakService } from 'keycloak-angular';
import { stringify } from '@angular/core/src/util';
import { ExceptionEvent } from '../enums/insights/exception-event';
import { SeverityLevel } from '@microsoft/applicationinsights-common';
import { AppInsightsService } from './app-insights-service';
function getCleanUsername(username: string) {
  return username.replace(/\./g, '');
}

declare global {
  // tslint:disable-next-line: interface-name
  interface Window {
    msCrypto: Crypto;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public loggedIn = new BehaviorSubject<boolean>(false);
  ssoUrl = environment.url.sso;
  apiURL = environment.url.api;

  get isLoggedInBehavior() {
    return this.loggedIn.asObservable();
  }

  constructor(
    private http: HttpClient,
    private idleTimeoutSvc: SessionTimeoutService,
    @Inject(DOCUMENT) private document: Document,
    private keycloakService: KeycloakService,
    private appInsightsService: AppInsightsService
  ) {
    this.loggedIn.next(this.isLoggedIn());
  }

  public isLoggedIn(): boolean {
    const objectToken = getLocalStorage('token');
    if (!objectToken) {
      return false;
    }
    if (!objectToken.expireAccess) {
      return false;
    }
    const current = moment.utc();
    const expire = moment.utc(objectToken.expireAccess);

    const diff = expire.diff(current, 'seconds');

    return diff > 10;
  }

  public getToken(): any {
    const token = getLocalStorage('token');
    return token ? token.access_token : null;
  }

  public getRefreshToken(): any {
    const token = getLocalStorage('token');
    return token ? token.refresh_token : null;
  }

  public getRandomState(): string {
    const validChars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const array = new Uint8Array(40);
    const crypto = window.crypto || window.msCrypto;
    crypto.getRandomValues(array);
    for (let i = 0; i < array.length; i++) {
      array[i] = validChars.charCodeAt(array[i] % validChars.length);
    }
    const randomState = String.fromCharCode.apply(null, array);
    return randomState;
  }

  public requestCode() {
    const randomState = this.getRandomState();
    localStorage.setItem('state', randomState);
    const query = new URLSearchParams();
    query.set('state', randomState);
    query.set('response_type', 'code');
    query.set('client_id', 'vs-site-client');
    query.set(
      'redirect_uri',
      `${this.document.location.origin}/${paths.identify.path}`
    );
    query.set('scope', 'openid profile User');
    this.document.location.href = `${
      environment.url.sso
    }/auth?${query.toString()}`;
  }

  public validateState(responseState: string): boolean {
    const state = localStorage.getItem('state');
    localStorage.removeItem('state');
    return state === responseState;
  }

  public identify(code: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };

    const body = new URLSearchParams();
    body.set('grant_type', 'authorization_code');
    body.set('code', code);
    body.set('client_id', 'vs-site-client');
    body.set('redirect_uri', `${this.document.location.origin}/auth`);
    body.set('scope', 'openid profile User');
    return this.http
      .post<any>(`${this.ssoUrl}/token`, body.toString(), httpOptions)
      .pipe(
        mergeMap(token => {
          return this.getUserInfoIdentity(token).pipe(
            map(userInfo => {
              setLocalStorage(key_user_info, userInfo);
              this.loggedIn.next(true);
            })
          );
        })
      );
  }

  getUserInfoIdentity(token) {
    token.dateAccess = moment.utc();
    token.expireAccess = moment.utc().add(token.expires_in, 'seconds');
    if (token.token_type && token.token_type === 'bearer') {
      setLocalStorage('token', token);
    }
    localStorage.removeItem('printdata');
    return this.userInfo(token);
  }

  public authenticate(username: string, password: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };

    const body = new URLSearchParams();
    const cleanUsername = getCleanUsername(username);
    body.set('grant_type', 'password');
    body.set('username', cleanUsername);
    body.set('password', password);
    body.set('client_id', 'vs-site-client');
    body.set('client_secret', 'aa17f0af-21cd-41a7-b0cf-a3bd250d9056');

    return this.http
      .post<any>(`${this.ssoUrl}/token`, body.toString(), httpOptions)
      .pipe(
        mergeMap(token =>
          this.getUserinfoAuthenticate(token, cleanUsername).pipe(
            map(userInfo => {
              setLocalStorage(key_user_info, userInfo);
              this.loggedIn.next(true);
            })
          )
        )
      );
  }

  getUserinfoAuthenticate(token, cleanUsername): Observable<any> {
    token.dateAccess = moment.utc();
    token.expireAccess = moment.utc().add(token.expires_in, 'seconds');
    if (token.token_type && token.token_type === 'bearer') {
      setLocalStorage('token', token);
      this.appInsightsService.trackEvent(Event.ceLoginSuccess);
      this.appInsightsService.trackPageView('Individual Insurance Summary View' as any, '/productos/individual/resumen');
    }
    localStorage.removeItem('printdata');

    const tokenData = {
      accessToken: token.access_token,
      expiresIn: token.expires_in,
      refreshExpiresIn: token.refresh_expires_in,
      refreshToken: token.refresh_token,
      tokenType: token.token_type,
      sessionState: token.session_state,
    };

    this.appInsightsService.tokenData = tokenData;
    return this.userInfo(token);
  }

  public refresh(timeOutExpire = false) {
    const storedToken = getLocalStorage('token');
    if (!storedToken) {
      return;
    }
    const now = moment.utc();

    if (
      timeOutExpire ||
      (storedToken &&
        storedToken.hasOwnProperty('expireAccess') &&
        moment.utc(storedToken.expireAccess).diff(now, 'seconds') <
          defaultSessionRefreshTime(storedToken))
    ) {
      // refresh if halfways to expiration or less

      this.doRefreshToken(storedToken);

      /* const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded'
        })
      };

      const body = new URLSearchParams();
      body.set('grant_type', 'refresh_token');
      body.set('client_id', 'vs-site-client');
      body.set('client_secret', 'aa17f0af-21cd-41a7-b0cf-a3bd250d9056');
      body.set('refresh_token', storedToken.refresh_token);
      return this.http
        .post<any>(`${this.ssoUrl}/token`, body.toString(), httpOptions)
        .pipe(
          share(),
          exhaustMap(
            token => {
              token.dateAccess = moment.utc();
              token.expireAccess = moment
                .utc()
                .add(token.expires_in, 'seconds');
              if (token.token_type && token.token_type === 'bearer') {
                localStorage.setItem('token', JSON.stringify(token));
              }
              return this.userInfo(token);
            },
            (token, userInfo) => {
              localStorage.setItem('userInfo', JSON.stringify(userInfo));
            }
          )
        )
        .subscribe(userinfo => {}, error => {}); */
    } else {
      this.setDataInsights(storedToken,  getLocalStorage(key_user_info));
    }
  }

  doRefreshToken(storedToken) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };

    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');
    body.set('client_id', 'vs-site-client');
    body.set('refresh_token', storedToken.refresh_token);
    return this.http
      .post<any>(`${this.ssoUrl}/token`, body.toString(), httpOptions)
      .pipe(
        share(),
        exhaustMap(token =>
          this.getUserInfo(token).pipe(
            map(userInfo => {
              setLocalStorage(key_user_info, userInfo);
              this.setDataInsights(token, userInfo);
            })
          )
        )
      )
      .subscribe(
        userinfo => {},
        error => {}
      );
  }

  getUserInfo(token): Observable<any> {
    this.idleTimeoutSvc.setLogout(false);
    this.idleTimeoutSvc.resetTimer();
    token.dateAccess = moment.utc();
    token.expireAccess = moment.utc().add(token.expires_in, 'seconds');
    if (token.token_type && token.token_type === 'bearer') {
      setLocalStorage('token', token);
    }
    return this.userInfo(token);
  }

  userInfo(token): Observable<any> {
    const decoded = jwt_decode(token.access_token);
    return of(decoded);
  }

  logout(): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };

    const token = getLocalStorage('token');

    const body = new URLSearchParams();
    body.set('refresh_token', token.refresh_token);
    body.set('client_id', 'vs-site-client');
    body.set('client_secret', 'aa17f0af-21cd-41a7-b0cf-a3bd250d9056');

    localStorage.clear();
    sessionStorage.clear();
    this.idleTimeoutSvc.stopTimer();
    this.loggedIn.next(false);
    this.keycloakService.logout();
    return new Observable(observer => {
      observer.next();
      observer.complete();
    });
    // this.http.get<any>(`${this.ssoUrl}/logout`, httpOptions);
  }

  CerrarSesion() {
    removeLocalStorage('token');
    removeLocalStorage('userInfo');
    removeLocalStorage('SC');
    removeLocalStorage('token_sso');
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('token_sso');
    localStorage.clear();
    this.loggedIn.next(false);
    this.loggedIn.unsubscribe();
  }

  public logoutRedirect() {
    localStorage.clear();
    const query = new URLSearchParams();
    // query.set(
    //   'redirect_uri',
    //   `${this.document.location.origin}` + '/cerrarSesion'
    // );
    query.set('redirect_uri', `${this.document.location.origin}`);
    this.document.location.href = `${
      environment.url.sso
    }/logout?${query.toString()}`;
    // http://auth-server/auth/realms/{realm-name}/protocol/openid-connect/logout?redirect_uri=encodedRedirectUri
  }

  getUsername(): string {
    const userInfo = getLocalStorage(key_user_info);
    return userInfo ? userInfo.name : '';
  }

  getRut(): string {
    const userInfo = getLocalStorage(key_user_info);
    return userInfo ? userInfo.rut : '';
  }

  getEmail(): string {
    const userInfo = getLocalStorage(key_user_info);
    return userInfo ? userInfo.email : '';
  }

  getPersistencia(): string {
    const userInfo = getLocalStorage(key_user_info);
    return userInfo ? userInfo.persistencia : '';
  }

  getClientId(dominio: string): string {
    let respuesta: string;

    switch (dominio) {
      case 'Portabilidad':
        respuesta = environment.ClientIdPortabilidad;
        break;
      case 'Corredores':
        respuesta = environment.ClientIdCorredores;
        break;
      case 'SitioPrivado':
        respuesta = environment.ClientIdSitioPrivado;
        break;
      default:
        respuesta = environment.ClientId;
        break;
    }
    return respuesta;
  }

  requestPassword(username: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-IBM-Client-Id': environment.ClientId,
      }),
    };
    const body = {
      txtRut: getPureRut(username) + getRutDigit(username).toUpperCase(),
    };
    return this.http.post<any>(
      `${this.apiURL}/ind/Clientes/Postacceso/RecuperarClave/`,
      body,
      httpOptions
    );
  }

  getUserData(username: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'X-IBM-Client-Id': environment.ClientId,
      }),
    };
    const rut = getCleanUsername(username);
    return this.http.get<any>(
      `${this.apiURL}/ind/Clientes/Acceso/ObtenerUsuario/${getPureRut(
        rut
      )}${getRutDigit(rut)}`,
      httpOptions
    );
  }

  validateUserDate(rut: string, fn: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-IBM-Client-Id': environment.ClientId,
      }),
    };
    return this.http.get<any>(
      `${this.apiURL}/ind/Clientes/Acceso/ValidarDatosUsuario/${getFullRut(
        rut
      )}/${moment(fn).format('DD-MM-YYYY')}`,
      httpOptions
    );
  }

  validateTempPass(rut: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-IBM-Client-Id': environment.ClientId,
      }),
    };

    return this.http.get<any>(
      `${
        this.apiURL
      }/ind/Usuarios/Modificarcontrasena/ValidarClave/${getFullRut(rut)}`,
      httpOptions
    );
  }

  validarFirmaDigital(username: string): Observable<any> {
    const cleanUsername = getFullRut(getCleanUsername(username));
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
        LR_USUARIO: getCleanUsername(username),
        Sistema: 'Clientes',
        LR_CODINTER: '00000000',
      }),
    };
    return this.http.get<any>(
      `${this.apiURL}/ind/Polizas/Traslados/ValidarConvenioPrestacionServicioDistancia/${cleanUsername}/${cleanUsername}`,
      httpOptions
    );
  }

  firmarDigital() {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-IBM-Client-Id': environment.ClientId,
      }),
    };
    const body = {};
    return this.http.post<any>(`${this.apiURL}/ind/`, body, httpOptions);
  }

  getPersistenceToken() {
    const objectToken = getLocalStorage('userInfo');
    return objectToken.persistencia;
  }

  getDatosClienteFirmaDigital(): Observable<IDatosCliente> {
    const userInfo = getLocalStorage(key_user_info);
    return this.http
      .get<IDatosCliente>(
        `${this.apiURL}/ind/Clientes/Informacion/${getPureRut(
          userInfo.rut
        )}/${getRutDigit(userInfo.rut)}`
      )
      .pipe(
        map(res => {
          res.RUT = userInfo.rut;
          return res;
        })
      );
  }

  putDatosContratante(datosContratante: any) {
    const userInfo = getLocalStorage(key_user_info);
    return this.http
      .put<any>(
        `${this.apiURL}/ind/Clientes/Informacion/${getPureRut(
          userInfo.rut
        )}/${getRutDigit(userInfo.rut)}`,
        datosContratante
      )
      .pipe(
        map(res => {
          return res;
        })
      );
  }

  postMandatoFirma(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.apiURL}/com/Mandato/Firma/procesoMandatoFirma`, data)
      .pipe(
        map(res => {
          return res;
        })
      );
  }

  public LogoutClientes(timeOutExpire = false) {
    const storedToken = getLocalStorage('token');
    if (!storedToken) {
      return;
    }
    const now = moment.utc();

    if (
      timeOutExpire ||
      (storedToken &&
        storedToken.hasOwnProperty('expireAccess') &&
        defaultSessionRefreshTime(storedToken)) > 1740
    ) {
      // refresh if halfways to expiration or less

      this.doRefreshToken(storedToken);

      /* const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded'
        })
      };

      const body = new URLSearchParams();
      body.set('grant_type', 'refresh_token');
      body.set('client_id', 'vs-site-client');
      body.set('client_secret', 'aa17f0af-21cd-41a7-b0cf-a3bd250d9056');
      body.set('refresh_token', storedToken.refresh_token);
      return this.http
        .post<any>(`${this.ssoUrl}/token`, body.toString(), httpOptions)
        .pipe(
          share(),
          exhaustMap(
            token => {
              token.dateAccess = moment.utc();
              token.expireAccess = moment
                .utc()
                .add(token.expires_in, 'seconds');
              if (token.token_type && token.token_type === 'bearer') {
                localStorage.setItem('token', JSON.stringify(token));
              }
              return this.userInfo(token);
            },
            (token, userInfo) => {
              localStorage.setItem('userInfo', JSON.stringify(userInfo));
            }
          )
        )
        .subscribe(userinfo => {}, error => {}); */
    }
  }

  obtenerDatos(objToken) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };
    const body = new URLSearchParams();
    body.set('access_token', objToken.access_token);
    return this.http
      .post<any>(`${this.ssoUrl}/userinfo`, body.toString(), httpOptions)
      .pipe(
        map(data => {
          this.setDataInsights(objToken, data);
          setLocalStorage(key_user_info, data);
          this.getUserinfoAuthenticate(objToken, data.rut);
          // console.log(data);
          this.loggedIn.next(true);
        })
      );
  }

  private setDataInsights(objToken: any, data: any) {
    const tokenData = {
      accessToken: objToken.access_token,
      expiresIn: objToken.expires_in,
      refreshExpiresIn: objToken.refresh_expires_in,
      refreshToken: objToken.refresh_token,
      tokenType: objToken.token_type,
      sessionState: objToken.session_state,
    };
    this.appInsightsService.userInfo = {
      sub: data.sub,
      name: data.name,
      preferredUsername: data.preferred_username,
      givenName: data.given_name,
      familyName: data.family_name,
      email: data.email,
      business: data.lineasnegocios,
      policy: data.polizas,
      tokenData
    };
    this.appInsightsService.tokenData = tokenData;
    this.appInsightsService.authenticate(data.preferred_username);
  }

  authenticateSso(tokenSso) {
    const objToken = {
      'allowed-origins': ['*'],
      access_token: tokenSso.token,
      expires_in: 1200,
      refresh_expires_in: 1200,
      refresh_token: tokenSso.refreshToken,
      token_parsed: tokenSso.tokenParsed,
      scope: 'Sites-Clients', // keykklo._instance.tokenParsed.scope,
      session_state: tokenSso.tokenParsed.session_state,
      token_type: 'bearer', // tokenSso.tokenParsed.typ.,
      dateAccess: 0,
      typ: 'bearer',
      iss: environment.url.sso
    };

    return this.obtenerDatos(objToken);
  }
}
